import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Play, Download, RefreshCw, ChevronDown, ChevronUp, Plus, X,
  AlertCircle, CheckCircle, Database, Filter, Table, Code, Eye, Copy,
  ArrowUpDown, ArrowUp, ArrowDown, Columns, Hash, Clock, Trash2, Save,
  Terminal, Zap, Info, Sparkles, Send, ThumbsUp, ThumbsDown, BarChart2,
  Users, TrendingUp, List, Edit3, MessageSquare, Lightbulb, Loader
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

/**
 * QueryExplorer - Dynamic query interface for the CDP unified table.
 * 
 * Usage modes:
 * 1. Standalone tab: <QueryExplorer session={session} />
 * 2. Within a segment: <QueryExplorer session={session} segmentId="xxx" segmentName="High Value" />
 * 
 * When segmentId is provided, the filter is ANDed with the segment's own conditions.
 */
const QueryExplorer = ({ session, segmentId = null, segmentName = null, onSegmentSaved = null }) => {
  // Query state
  const [queryMode, setQueryMode] = useState('text'); // 'text' or 'visual'
  const [textFilter, setTextFilter] = useState('');
  const [conditions, setConditions] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]); // empty = all
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Data state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]); // [{name, category, hint}] or just strings
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnMap, setColumnMap] = useState({}); // {col_name: {category, hint, clickhouse_type}}

  // UI state
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showWhereClause, setShowWhereClause] = useState(false);
  const [queryHistory, setQueryHistory] = useState([]);
  const [parseError, setParseError] = useState('');
  const textInputRef = useRef(null);

  // Autocomplete state
  const [acVisible, setAcVisible] = useState(false);
  const [acItems, setAcItems] = useState([]);
  const [acIndex, setAcIndex] = useState(0);
  const [acWordStart, setAcWordStart] = useState(0);
  const acRef = useRef(null);

  // Save as Segment state
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [saveSegmentName, setSaveSegmentName] = useState('');
  const [saveSegmentDesc, setSaveSegmentDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);

  // ─── AI Query State ───────────────────────────────────────
  const [aiAvailable, setAiAvailable] = useState(null); // null=checking, true/false
  const [aiBackendInfo, setAiBackendInfo] = useState(null);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiShowSql, setAiShowSql] = useState(false);
  const [aiConversationId, setAiConversationId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiHistory, setAiHistory] = useState([]); // conversation messages
  const [aiFeedbackGiven, setAiFeedbackGiven] = useState({}); // { queryId: score }
  const aiInputRef = useRef(null);
  const aiScrollRef = useRef(null);

  const isSegmentMode = !!segmentId;

  // Load available columns on mount
  useEffect(() => {
    loadColumns();
    checkAiBackend();
  }, [session?.session_id]);

  const loadColumns = async () => {
    try {
      setColumnsLoading(true);
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/columns`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const cols = data.columns || [];
        // Handle both enhanced format [{name, category, hint}] and legacy format [string]
        if (cols.length > 0 && typeof cols[0] === 'object') {
          setAvailableColumns(cols.map(c => c.name).sort((a, b) => a.localeCompare(b)));
          const map = {};
          cols.forEach(c => { map[c.name] = { category: c.category, hint: c.hint, type: c.clickhouse_type }; });
          setColumnMap(map);
        } else {
          setAvailableColumns([...cols].sort((a, b) => a.localeCompare(b)));
          setColumnMap({});
        }
      }
    } catch (err) {
      console.error('Error loading columns:', err);
    } finally {
      setColumnsLoading(false);
    }
  };

  // ─── AI Backend Detection ─────────────────────────────────
  const checkAiBackend = async () => {
    try {
      const token = getCurrentToken();
      const resp = await apiCall('/api/cdp/nl/config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiBackendInfo(data);
        const isUp = data.status === 'connected' || data.status === 'configured';
        setAiAvailable(isUp);
        if (isUp) loadAiSuggestions();
      } else {
        setAiAvailable(false);
      }
    } catch {
      setAiAvailable(false);
    }
  };

  const loadAiSuggestions = async () => {
    if (!session?.session_id) return;
    try {
      const token = getCurrentToken();
      const resp = await apiCall(`/api/cdp/sessions/${session.session_id}/nl/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiSuggestions(data.suggestions || data);
      }
    } catch {}
  };

  // ─── AI Query Execution ───────────────────────────────────
  const sendAiQuery = async (text = null) => {
    const q = (text || aiInput).trim();
    if (!q || aiLoading) return;

    // Add user message to history
    setAiHistory(prev => [...prev, { role: 'user', text: q, time: new Date() }]);
    setAiInput('');
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);

    try {
      const token = getCurrentToken();
      const resp = await apiCall(`/api/cdp/sessions/${session.session_id}/nl/query`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: q,
          conversation_id: aiConversationId,
          execute: true,
          limit: 100,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        setAiResult(data);
        setAiConversationId(data.conversation_id);
        setAiShowSql(true);

        // Add assistant message to history
        setAiHistory(prev => [...prev, {
          role: 'assistant',
          data: data,
          time: new Date()
        }]);

        // Populate the MAIN results table so data renders below in the standard results section
        if (data.results?.rows?.length > 0) {
          const rows = data.results.rows;
          const cols = data.results.columns || (rows.length > 0 ? Object.keys(rows[0]) : []);
          setResults({
            total_rows: data.result_count || rows.length,
            filtered_count: data.result_count || rows.length,
            rows_returned: rows.length,
            columns: cols,
            data: rows,
            where_clause: data.generated_sql || '',
            offset: 0,
            limit: rows.length,
          });
          setError(null);
        }

        // Scroll to bottom of AI area
        setTimeout(() => {
          aiScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const err = await resp.json().catch(() => ({ detail: 'Query failed' }));
        setAiError(err.detail || 'Query failed');
        setAiHistory(prev => [...prev, {
          role: 'error',
          text: err.detail || 'Query failed',
          time: new Date()
        }]);
      }
    } catch (e) {
      setAiError('Network error: ' + e.message);
      setAiHistory(prev => [...prev, {
        role: 'error',
        text: 'Network error: ' + e.message,
        time: new Date()
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const submitAiFeedback = async (queryId, score) => {
    if (!queryId) return;
    try {
      const token = getCurrentToken();
      await apiCall(`/api/cdp/sessions/${session.session_id}/nl/feedback`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query_id: queryId, score }),
      });
      setAiFeedbackGiven(prev => ({ ...prev, [queryId]: score }));
    } catch {}
  };

  // Push AI-generated SQL into the text query mode
  const applyAiSqlToTextMode = (sql) => {
    setTextFilter(sql);
    setQueryMode('text');
    // Auto-scroll to the text input
    setTimeout(() => textInputRef.current?.focus(), 100);
  };

  const startNewAiConversation = () => {
    setAiHistory([]);
    setAiResult(null);
    setAiError(null);
    setAiConversationId(null);
    setAiInput('');
    loadAiSuggestions();
  };

  // Execute query
  const executeQuery = useCallback(async (newOffset = 0) => {
    setError(null);
    setLoading(true);
    setOffset(newOffset);

    try {
      const token = getCurrentToken();

      if (isSegmentMode) {
        // Query within segment: GET with filter param
        const params = new URLSearchParams();
        if (textFilter.trim() && queryMode === 'text') {
          params.set('filter', textFilter.trim());
        }
        params.set('limit', String(limit));
        params.set('page', String(newOffset / limit || 0));
        if (sortBy) {
          params.set('sort_by', sortBy);
          params.set('sort_order', sortOrder);
        }
        if (selectedColumns.length > 0) {
          params.set('columns', selectedColumns.join(','));
        }

        const url = `/api/cdp/sessions/${session.session_id}/segments/${segmentId}/data?${params}`;
        const response = await apiCall(url, { headers: { 'Authorization': `Bearer ${token}` } });

        if (response.ok) {
          const data = await response.json();
          setResults({
            total_rows: data.total_count,
            filtered_count: data.filtered_count,
            rows_returned: data.rows_returned || data.sample?.length || 0,
            columns: data.sample?.length > 0 ? Object.keys(data.sample[0]) : [],
            data: data.sample || [],
            where_clause: data.applied_filter || '',
            offset: newOffset,
            limit: limit,
          });
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Query failed');
        }
      } else {
        // Direct query on unified table: POST
        const body = { limit, offset: newOffset };

        if (queryMode === 'text' && textFilter.trim()) {
          body.filter = textFilter.trim();
        } else if (queryMode === 'visual' && conditions.length > 0) {
          const validConditions = conditions.filter(c => c.column && c.operator && c.value !== '');
          if (validConditions.length > 0) {
            body.conditions = validConditions;
          }
        }

        if (selectedColumns.length > 0) body.columns = selectedColumns;
        if (sortBy) {
          body.sort_by = sortBy;
          body.sort_order = sortOrder;
        }

        const response = await apiCall(
          `/api/cdp/sessions/${session.session_id}/query`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }
        );

        if (response.ok) {
          const data = await response.json();
          setResults(data);

          // Add to history
          const filterText = body.filter || (body.conditions ? JSON.stringify(body.conditions) : 'All data');
          setQueryHistory(prev => {
            const entry = { filter: filterText, count: data.filtered_count, time: new Date() };
            return [entry, ...prev.filter(h => h.filter !== filterText)].slice(0, 20);
          });
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Query failed');
        }
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [session, segmentId, textFilter, conditions, selectedColumns, sortBy, sortOrder, limit, queryMode]);

  // Parse text to conditions (for switching modes)
  const parseTextToConditions = async () => {
    if (!textFilter.trim()) return;
    try {
      setParseError('');
      const token = getCurrentToken();
      const response = await apiCall(`/api/cdp/sessions/${session.session_id}/parse-query`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: textFilter })
      });
      if (response.ok) {
        const data = await response.json();
        setConditions(data.conditions || []);
        setParseError('');
      } else {
        const err = await response.json();
        setParseError(err.detail || 'Parse error');
      }
    } catch (e) {
      setParseError('Failed to parse query');
    }
  };

  // Keyboard shortcut
  // ==================== Autocomplete ====================
  const SQL_KEYWORDS = ['AND', 'OR', 'NOT', 'IN', 'BETWEEN', 'LIKE', 'IS', 'NULL', 'TRUE', 'FALSE', 'ASC', 'DESC'];
  const SQL_OPERATORS = ['=', '!=', '>', '<', '>=', '<='];

  const getWordAtCursor = (text, cursorPos) => {
    // Walk back from cursor to find start of current word
    let start = cursorPos;
    while (start > 0 && /[a-zA-Z0-9_]/.test(text[start - 1])) {
      start--;
    }
    const word = text.slice(start, cursorPos);
    return { word, start };
  };

  const computeAutocomplete = (text, cursorPos) => {
    const { word, start } = getWordAtCursor(text, cursorPos);
    if (word.length < 1) {
      setAcVisible(false);
      return;
    }

    const lower = word.toLowerCase();

    // Filter columns (sorted alphabetically at source)
    const colMatches = availableColumns
      .filter(c => !c.startsWith('_') && c.toLowerCase().includes(lower))
      .map(c => ({ label: c, type: 'column' }));

    // Filter SQL keywords
    const kwMatches = SQL_KEYWORDS
      .filter(k => k.toLowerCase().startsWith(lower) && k.toLowerCase() !== lower)
      .map(k => ({ label: k, type: 'keyword' }));

    const items = [...colMatches, ...kwMatches].slice(0, 10);

    if (items.length > 0) {
      setAcItems(items);
      setAcIndex(0);
      setAcWordStart(start);
      setAcVisible(true);
    } else {
      setAcVisible(false);
    }
  };

  const applyAutocomplete = (item) => {
    const el = textInputRef.current;
    if (!el) return;
    const before = textFilter.slice(0, acWordStart);
    const after = textFilter.slice(el.selectionStart);
    const suffix = item.type === 'column' ? ' ' : ' ';
    const newText = before + item.label + suffix + after;
    setTextFilter(newText);
    setAcVisible(false);
    // Restore cursor position after React re-render
    const newPos = acWordStart + item.label.length + suffix.length;
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setTextFilter(val);
    setParseError('');
    computeAutocomplete(val, e.target.selectionStart);
  };

  const handleKeyDown = (e) => {
    // Autocomplete navigation
    if (acVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAcIndex(prev => (prev + 1) % acItems.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAcIndex(prev => (prev - 1 + acItems.length) % acItems.length);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        // Only intercept Enter for autocomplete (not Ctrl+Enter for execute)
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          setAcVisible(false);
          executeQuery(0);
          e.preventDefault();
          return;
        }
        e.preventDefault();
        applyAutocomplete(acItems[acIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setAcVisible(false);
        return;
      }
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      executeQuery(0);
    }
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (acRef.current && !acRef.current.contains(e.target) &&
          textInputRef.current && !textInputRef.current.contains(e.target)) {
        setAcVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get current conditions (parse text filter if in text mode)
  const getCurrentConditions = async () => {
    if (queryMode === 'visual' && conditions.length > 0) {
      return conditions.filter(c => c.column && c.value !== '');
    }
    if (queryMode === 'text' && textFilter.trim()) {
      try {
        const token = getCurrentToken();
        const response = await apiCall(`/api/cdp/sessions/${session.session_id}/parse-query`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: textFilter })
        });
        if (response.ok) {
          const data = await response.json();
          return data.conditions || [];
        }
      } catch (e) { /* fall through */ }
    }
    return [];
  };

  // Save current query as a segment
  const saveAsSegment = async () => {
    if (!saveSegmentName.trim()) return;
    setSaving(true);
    setSaveSuccess(null);

    try {
      const parsedConditions = await getCurrentConditions();
      if (parsedConditions.length === 0) {
        setSaveSuccess({ ok: false, msg: 'No valid conditions to save. Run a query with filters first.' });
        setSaving(false);
        return;
      }

      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/segments/create`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: session.session_id,
            segment_name: saveSegmentName.trim(),
            description: saveSegmentDesc.trim() || null,
            conditions: parsedConditions
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSaveSuccess({
          ok: true,
          msg: `Segment "${saveSegmentName}" saved with ${data.customer_count?.toLocaleString() || 0} customers`
        });
        setSaveSegmentName('');
        setSaveSegmentDesc('');
        if (onSegmentSaved) onSegmentSaved();
        // Auto-close after 3 seconds
        setTimeout(() => { setSaveSuccess(null); setShowSavePanel(false); }, 3000);
      } else {
        const err = await response.json();
        setSaveSuccess({ ok: false, msg: err.detail || 'Failed to save segment' });
      }
    } catch (e) {
      setSaveSuccess({ ok: false, msg: 'Network error: ' + e.message });
    } finally {
      setSaving(false);
    }
  };

  // Pagination helpers
  const currentPage = Math.floor(offset / limit);
  const totalPages = results ? Math.ceil(results.filtered_count / limit) : 0;

  // System columns to hide
  const systemColumns = [
    '_customer_key', '_version', '_version_type', '_source_campaign_id',
    '_source_file_id', '_created_at', '_updated_at', '_tenant_id',
    '_is_new', '_is_updated', '_changed_fields'
  ];

  const filterSystemCols = (cols) => cols.filter(c => !systemColumns.includes(c));

  const visibleResultColumns = results?.data?.length > 0
    ? filterSystemCols(results.columns || Object.keys(results.data[0]))
    : [];

  // Handle sort click on column header
  const handleSortClick = (col) => {
    if (sortBy === col) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  // ==================== Render ====================

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Terminal className="h-5 w-5 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isSegmentMode ? `Filter: ${segmentName}` : 'Query Explorer'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isSegmentMode
                    ? 'Apply additional filters on top of segment conditions'
                    : 'Query your unified customer data directly — build dynamic segments on the fly'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <span>Ctrl+Enter to run</span>
            </div>
          </div>
        </div>

        {/* Query Input Area */}
        <div className="px-6 py-4 space-y-3">
          {/* Mode Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setQueryMode('text')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                queryMode === 'text' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center space-x-1"><Code className="h-3 w-3" /><span>Text Query</span></span>
            </button>
            <button
              onClick={() => { setQueryMode('visual'); if (textFilter.trim()) parseTextToConditions(); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                queryMode === 'visual' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center space-x-1"><Filter className="h-3 w-3" /><span>Visual Builder</span></span>
            </button>

            {/* AI Query — always visible, disabled when LLM backend unavailable */}
            <div className="ml-1 pl-2 border-l border-gray-300">
            <button
              onClick={() => { if (aiAvailable) { setQueryMode('ai'); if (!aiSuggestions) loadAiSuggestions(); } }}
              disabled={!aiAvailable}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border-2 ${
                queryMode === 'ai'
                  ? 'bg-blue-100 text-blue-700 border-blue-500'
                  : aiAvailable
                    ? 'bg-gray-100 text-gray-700 border-gray-400 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-400 border-dashed border-gray-300 cursor-not-allowed'
              }`}
              title={!aiAvailable ? 'AI backend not configured — configure llama.cpp, Ollama, or Anthropic to enable' : 'Query with natural language'}
            >
              <span className="flex items-center space-x-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                <span>AI Query</span>
                {aiAvailable === false && (
                  <span className="text-[9px] text-gray-400 font-normal">(offline)</span>
                )}
              </span>
            </button>
            </div>

            <div className="flex-1" />

            {/* Column Picker Toggle */}
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                selectedColumns.length > 0 ? 'border-blue-300 bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center space-x-1">
                <Columns className="h-3 w-3" />
                <span>{selectedColumns.length > 0 ? `${selectedColumns.length} columns` : 'All columns'}</span>
              </span>
            </button>

            {/* Sort */}
            {sortBy && (
              <span className="px-2 py-1 text-xs bg-gray-100 rounded flex items-center space-x-1">
                <ArrowUpDown className="h-3 w-3" />
                <span>{sortBy} {sortOrder}</span>
                <button onClick={() => { setSortBy(''); setSortOrder('ASC'); }} className="ml-1 text-gray-400 hover:text-gray-600">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* Limit */}
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-2 py-1.5 text-xs border rounded-lg"
            >
              <option value={25}>25 rows</option>
              <option value={50}>50 rows</option>
              <option value={100}>100 rows</option>
              <option value={250}>250 rows</option>
              <option value={500}>500 rows</option>
            </select>
          </div>

          {/* Text Query Mode */}
          {queryMode === 'text' && (
            <div className="space-y-2">
              <div className="relative">
                <textarea
                  ref={textInputRef}
                  value={textFilter}
                  onChange={handleTextChange}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => computeAutocomplete(e.target.value, e.target.selectionStart)}
                  className="w-full px-4 py-3 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={2}
                  placeholder={
                    isSegmentMode
                      ? "e.g. city = 'Mumbai' AND purchase_amount > 500"
                      : "e.g. country IN ('US', 'UK') AND purchase_amount BETWEEN 100 AND 1000 AND is_active = true"
                  }
                  autoComplete="off"
                />
                {textFilter && (
                  <button
                    onClick={() => { setTextFilter(''); setParseError(''); setAcVisible(false); }}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {/* Autocomplete dropdown */}
                {acVisible && acItems.length > 0 && (
                  <div
                    ref={acRef}
                    className="absolute z-50 left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  >
                    {acItems.map((item, idx) => (
                      <button
                        key={`${item.label}-${idx}`}
                        onMouseDown={(e) => { e.preventDefault(); applyAutocomplete(item); }}
                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between ${
                          idx === acIndex
                            ? 'bg-blue-50 text-blue-900'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="font-mono truncate">{item.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ml-2 flex-shrink-0 ${
                          item.type === 'column'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-purple-100 text-purple-600'
                        }`}>
                          {item.type === 'column' ? 'column' : 'SQL'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {parseError && (
                <div className="flex items-center space-x-2 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>{parseError}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {[
                  "city = 'Mumbai'",
                  "purchase_amount > 500",
                  "country IN ('US', 'UK')",
                  "email LIKE '%@gmail.com'",
                  "age BETWEEN 25 AND 40",
                  "is_active = true"
                ].map((hint, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setTextFilter(prev => prev ? `${prev} AND ${hint}` : hint);
                      textInputRef.current?.focus();
                    }}
                    className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-blue-50 hover:text-blue-700 transition-colors"
                  >
                    {hint}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Visual Builder Mode */}
          {queryMode === 'visual' && (
            <VisualConditionBuilder
              conditions={conditions}
              setConditions={setConditions}
              availableColumns={availableColumns}
              columnsLoading={columnsLoading}
              columnMap={columnMap}
            />
          )}

          {/* ─── AI Query Mode ────────────────────────────────── */}
          {queryMode === 'ai' && aiAvailable && (
            <div className="space-y-3">
              {/* Conversation Area */}
              {aiHistory.length > 0 && (
                <div className="max-h-[480px] overflow-y-auto space-y-3 pr-1">
                  {aiHistory.map((msg, idx) => (
                    <div key={idx}>
                      {/* User message */}
                      {msg.role === 'user' && (
                        <div className="flex justify-end">
                          <div className="bg-blue-600 text-white px-4 py-2 rounded-2xl rounded-br-md max-w-[80%] text-sm">
                            {msg.text}
                          </div>
                        </div>
                      )}

                      {/* Error message */}
                      {msg.role === 'error' && (
                        <div className="flex justify-start">
                          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-2xl rounded-bl-md max-w-[80%] text-sm flex items-center space-x-2">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <span>{msg.text}</span>
                          </div>
                        </div>
                      )}

                      {/* Assistant message with results */}
                      {msg.role === 'assistant' && msg.data && (
                        <AiResultCard
                          data={msg.data}
                          feedbackGiven={aiFeedbackGiven[msg.data.query_id]}
                          onFeedback={(score) => submitAiFeedback(msg.data.query_id, score)}
                          onUseSql={() => applyAiSqlToTextMode(msg.data.generated_sql)}
                          onFollowUp={(q) => sendAiQuery(q)}
                          systemColumns={systemColumns}
                        />
                      )}
                    </div>
                  ))}
                  <div ref={aiScrollRef} />
                </div>
              )}

              {/* AI Suggestions — shown when no conversation yet */}
              {aiHistory.length === 0 && aiSuggestions && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 flex items-center space-x-1">
                    <Lightbulb className="h-3 w-3" />
                    <span>Try asking in plain English:</span>
                  </p>
                  {(Array.isArray(aiSuggestions) ? aiSuggestions : []).map((group, gi) => (
                    <div key={gi}>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{group.category}</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {(group.queries || []).map((q, qi) => (
                          <button
                            key={qi}
                            onClick={() => sendAiQuery(q)}
                            className="text-left px-3 py-2 text-xs bg-gray-50 text-gray-700 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-colors"
                          >
                            <span className="flex items-center space-x-1.5">
                              <MessageSquare className="h-3 w-3 flex-shrink-0 text-gray-400" />
                              <span className="truncate">{q}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Loading indicator */}
              {aiLoading && (
                <div className="flex items-center space-x-2 text-sm text-violet-600 py-2">
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Generating query...</span>
                </div>
              )}

              {/* AI Input Bar */}
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <input
                    ref={aiInputRef}
                    type="text"
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendAiQuery();
                      }
                    }}
                    className="w-full pl-4 pr-10 py-2.5 border border-violet-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-violet-50/50 placeholder-gray-400"
                    placeholder="Ask in plain English, e.g. &quot;How many customers from Mumbai spent over ₹5000?&quot;"
                    disabled={aiLoading}
                    autoComplete="off"
                  />
                  <button
                    onClick={() => sendAiQuery()}
                    disabled={!aiInput.trim() || aiLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-violet-600 hover:text-violet-800 disabled:text-gray-300 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                {aiHistory.length > 0 && (
                  <button
                    onClick={startNewAiConversation}
                    className="px-3 py-2.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg border transition-colors whitespace-nowrap"
                    title="Start new conversation"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Backend info */}
              <div className="flex items-center justify-between text-[10px] text-gray-400">
                <div className="flex items-center space-x-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500"></span>
                  <span>AI Mode</span>
                  <span className="hidden">{aiBackendInfo?.backend || 'LLM'}{aiBackendInfo?.model ? ` · ${aiBackendInfo.model}` : ''}</span>
                </div>
                <span>AI-generated queries may need review</span>
              </div>
            </div>
          )}

          {/* Column Picker */}
          {showColumnPicker && (
            <ColumnPicker
              availableColumns={availableColumns.length > 0 ? availableColumns : (results?.columns || [])}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              systemColumns={systemColumns}
              onClose={() => setShowColumnPicker(false)}
            />
          )}

          {/* Action Bar — hidden in AI mode since it has its own execution */}
          {queryMode !== 'ai' && (
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => executeQuery(0)}
                disabled={loading}
                className="flex items-center space-x-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                <span>{loading ? 'Running...' : 'Run Query'}</span>
              </button>

              {(textFilter || conditions.length > 0) && (
                <button
                  onClick={() => { setTextFilter(''); setConditions([]); setError(null); setParseError(''); setResults(null); }}
                  className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Clear
                </button>
              )}

              {/* Save as Segment — only in standalone mode, after running a query with filters */}
              {!isSegmentMode && results && (textFilter || conditions.length > 0) && (
                <button
                  onClick={() => setShowSavePanel(!showSavePanel)}
                  className={`flex items-center space-x-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                    showSavePanel
                      ? 'bg-green-100 text-green-700'
                      : 'text-green-700 hover:bg-green-50 border border-green-300'
                  }`}
                >
                  <Save className="h-4 w-4" />
                  <span>Save as Segment</span>
                </button>
              )}
            </div>

            {results && (
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                {results.total_rows !== results.filtered_count && (
                  <span>
                    <span className="font-semibold text-blue-600">{results.filtered_count?.toLocaleString()}</span>
                    <span> of {results.total_rows?.toLocaleString()} rows matched</span>
                  </span>
                )}
                {results.total_rows === results.filtered_count && (
                  <span>
                    <span className="font-semibold text-gray-700">{results.total_rows?.toLocaleString()}</span>
                    <span> total rows</span>
                  </span>
                )}
              </div>
            )}
          </div>
          )}

          {/* Save as Segment Panel */}
          {showSavePanel && (
            <div className="mt-3 border border-green-200 rounded-lg bg-green-50 p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Save Current Query as Segment</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    This will save your current filter as a reusable customer segment
                    {results?.filtered_count != null && (
                      <span> — <span className="font-medium text-green-700">{results.filtered_count.toLocaleString()}</span> customers will be included</span>
                    )}
                  </p>
                </div>
                <button onClick={() => { setShowSavePanel(false); setSaveSuccess(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {saveSuccess && (
                <div className={`mb-3 flex items-center space-x-2 px-3 py-2 rounded-lg text-sm ${
                  saveSuccess.ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {saveSuccess.ok ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                  <span>{saveSuccess.msg}</span>
                </div>
              )}

              <div className="flex items-end space-x-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Segment Name *</label>
                  <input
                    type="text"
                    value={saveSegmentName}
                    onChange={(e) => setSaveSegmentName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., High-Value US Customers"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={saveSegmentDesc}
                    onChange={(e) => setSaveSegmentDesc(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Optional description..."
                  />
                </div>
                <button
                  onClick={saveAsSegment}
                  disabled={saving || !saveSegmentName.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2 whitespace-nowrap"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>{saving ? 'Saving...' : 'Save Segment'}</span>
                </button>
              </div>

              {/* Show the filter that will be saved */}
              <div className="mt-3 px-3 py-2 bg-white border rounded text-xs font-mono text-gray-600">
                <span className="text-gray-400">Filter: </span>
                {textFilter || (conditions.length > 0
                  ? conditions.filter(c => c.column).map((c, i) =>
                      `${i > 0 ? ` ${c.logic_operator || 'AND'} ` : ''}${c.column} ${c.operator} ${JSON.stringify(c.value)}`
                    ).join('')
                  : 'All data'
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <QueryErrorDisplay
          error={error}
          columnMap={columnMap}
          textFilter={textFilter}
          conditions={conditions}
          queryMode={queryMode}
        />
      )}

      {/* Results */}
      {results && !error && (
        <div className="bg-white rounded-lg shadow">
          {/* Results Header */}
          <div className="px-6 py-3 border-b flex items-center justify-between bg-gray-50">
            <div className="flex items-center space-x-3">
              <Table className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {results.rows_returned} rows returned
              </span>
              {results.where_clause && results.where_clause !== '1=1' && (
                <button
                  onClick={() => setShowWhereClause(!showWhereClause)}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                >
                  <Code className="h-3 w-3" />
                  <span>{showWhereClause ? 'Hide' : 'Show'} WHERE</span>
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => executeQuery(offset)}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* WHERE clause display */}
          {showWhereClause && results.where_clause && (
            <div className="px-6 py-2 border-b bg-gray-900">
              <code className="text-xs text-green-400 font-mono">WHERE {results.where_clause}</code>
            </div>
          )}

          {/* Data Table */}
          {results.data?.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-400 w-12">#</th>
                      {visibleResultColumns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                          onClick={() => handleSortClick(col)}
                        >
                          <span className="flex items-center space-x-1">
                            <span>{col}</span>
                            {sortBy === col ? (
                              sortOrder === 'ASC' ? <ArrowUp className="h-3 w-3 text-blue-500" /> : <ArrowDown className="h-3 w-3 text-blue-500" />
                            ) : (
                              <ArrowUpDown className="h-3 w-3 text-gray-300" />
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {results.data.map((row, rowIdx) => (
                      <tr key={rowIdx} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-3 py-2 text-xs text-gray-400 font-mono">{offset + rowIdx + 1}</td>
                        {visibleResultColumns.map((col) => (
                          <td
                            key={col}
                            className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap max-w-xs truncate"
                            title={row[col] != null ? String(row[col]) : ''}
                          >
                            {row[col] == null ? (
                              <span className="text-gray-300 italic text-xs">null</span>
                            ) : typeof row[col] === 'boolean' ? (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${row[col] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {row[col] ? 'true' : 'false'}
                              </span>
                            ) : (
                              String(row[col])
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{offset + 1}</span>–
                  <span className="font-medium">{Math.min(offset + results.rows_returned, results.filtered_count)}</span>
                  {' '}of <span className="font-medium">{results.filtered_count?.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => executeQuery(0)}
                    disabled={currentPage === 0 || loading}
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => executeQuery(Math.max(0, offset - limit))}
                    disabled={currentPage === 0 || loading}
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    Page {currentPage + 1} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => executeQuery(offset + limit)}
                    disabled={currentPage >= totalPages - 1 || loading}
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => executeQuery((totalPages - 1) * limit)}
                    disabled={currentPage >= totalPages - 1 || loading}
                    className="px-3 py-1 border rounded text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Database className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No results found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state - no query run yet */}
      {!results && !loading && !error && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium text-gray-700">
            {isSegmentMode ? 'Filter segment data' : 'Explore your customer data'}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {isSegmentMode
              ? 'Write a filter above and click Run to drill into this segment'
              : 'Write a query or use the visual builder, then click Run. Or just hit Run to browse all data.'}
          </p>
          {!isSegmentMode && (
            <button
              onClick={() => executeQuery(0)}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Browse All Data
            </button>
          )}
        </div>
      )}

      {/* Query History (standalone mode only) */}
      {!isSegmentMode && queryHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-3 border-b flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 flex items-center space-x-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span>Recent Queries</span>
            </span>
            <button
              onClick={() => setQueryHistory([])}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          </div>
          <div className="divide-y max-h-48 overflow-y-auto">
            {queryHistory.map((entry, idx) => (
              <button
                key={idx}
                onClick={() => { setTextFilter(entry.filter); setQueryMode('text'); }}
                className="w-full px-6 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <code className="text-xs text-gray-600 font-mono truncate flex-1">{entry.filter}</code>
                <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
                  <span className="text-xs text-blue-600 font-medium">{entry.count?.toLocaleString()} rows</span>
                  <span className="text-xs text-gray-400">{entry.time.toLocaleTimeString()}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== AI Result Card ====================

const AI_INTENT_CONFIG = {
  count:     { icon: Hash,       color: 'bg-blue-100 text-blue-700',     label: 'Count' },
  list:      { icon: List,       color: 'bg-indigo-100 text-indigo-700', label: 'List' },
  segment:   { icon: Users,      color: 'bg-purple-100 text-purple-700', label: 'Segment' },
  aggregate: { icon: BarChart2,  color: 'bg-emerald-100 text-emerald-700', label: 'Aggregate' },
  compare:   { icon: TrendingUp, color: 'bg-orange-100 text-orange-700', label: 'Compare' },
  trend:     { icon: TrendingUp, color: 'bg-rose-100 text-rose-700',     label: 'Trend' },
};

const AiResultCard = ({ data, feedbackGiven, onFeedback, onUseSql, onFollowUp, systemColumns = [] }) => {
  const [showSql, setShowSql] = useState(false);
  const [sqlCopied, setSqlCopied] = useState(false);

  const intentCfg = AI_INTENT_CONFIG[data.intent] || { icon: Database, color: 'bg-gray-100 text-gray-700', label: data.intent || 'Query' };
  const IntentIcon = intentCfg.icon;

  const copySql = () => {
    navigator.clipboard.writeText(data.generated_sql || '');
    setSqlCopied(true);
    setTimeout(() => setSqlCopied(false), 2000);
  };

  return (
    <div className="flex justify-start">
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md max-w-full w-full shadow-sm overflow-hidden">
        {/* Explanation header */}
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-start justify-between">
            <p className="text-sm text-gray-800 flex-1 leading-relaxed">{data.explanation || 'Query executed'}</p>
            <div className="flex items-center space-x-1.5 ml-3 flex-shrink-0">
              <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${intentCfg.color}`}>
                <IntentIcon className="h-3 w-3" />
                <span>{intentCfg.label}</span>
              </span>
              {data.confidence != null && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  data.confidence >= 0.8 ? 'bg-green-100 text-green-700' :
                  data.confidence >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {Math.round(data.confidence * 100)}%
                </span>
              )}
            </div>
          </div>

          {/* Assumptions */}
          {data.assumptions?.length > 0 && (
            <div className="flex items-start space-x-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-100 rounded-lg hidden">
              <AlertCircle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-700">
                {data.assumptions.join(' • ')}
              </p>
            </div>
          )}

          {/* Stats row */}
          <div className="flex items-center space-x-3 text-[11px] text-gray-400">
            {data.result_count != null && (
              <span className="font-medium text-gray-600">{data.result_count.toLocaleString()} rows</span>
            )}
            {data.execution_ms != null && (
              <span>{data.execution_ms}ms</span>
            )}
            {data.model_used && (
              <span>{data.model_used}</span>
            )}
          </div>
        </div>

        {/* SQL Block — collapsible */}
        <div className="border-t border-gray-100">
          <button
            onClick={() => setShowSql(!showSql)}
            className="w-full px-4 py-2 flex items-center justify-between text-xs text-gray-500 hover:bg-gray-50 transition-colors hidden"
          >
            <span className="flex items-center space-x-1.5">
              <Code className="h-3 w-3" />
              <span>Generated SQL</span>
            </span>
            {showSql ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          {showSql && data.generated_sql && (
            <div className="px-4 pb-3 space-y-2">
              <pre className="bg-gray-900 text-green-400 px-3 py-2 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                {data.generated_sql}
              </pre>
              <div className="flex items-center space-x-2">
                <button
                  onClick={copySql}
                  className="flex items-center space-x-1 px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                >
                  {sqlCopied ? <CheckCircle className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  <span>{sqlCopied ? 'Copied' : 'Copy'}</span>
                </button>
                <button
                  onClick={onUseSql}
                  className="flex items-center space-x-1 px-2 py-1 text-[10px] text-violet-600 hover:text-violet-800 hover:bg-violet-50 rounded transition-colors"
                >
                  <Edit3 className="h-3 w-3" />
                  <span>Edit in Text Query</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results rendered in main table below — show count indicator here */}
        {(data.result_count != null && data.result_count > 0) && (
          <div className="border-t border-gray-100 px-4 py-2 flex items-center space-x-1.5 text-xs text-blue-600">
            <Table className="h-3 w-3" />
            <span>{data.result_count.toLocaleString()} rows — see results below ↓</span>
          </div>
        )}

        {/* Footer: Feedback + Follow-ups */}
        <div className="border-t border-gray-100 px-4 py-2.5 space-y-2">
          {/* Feedback */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => onFeedback(1)}
                disabled={feedbackGiven != null}
                className={`p-1.5 rounded transition-colors ${
                  feedbackGiven === 1 ? 'bg-green-100 text-green-600' :
                  feedbackGiven != null ? 'text-gray-200 cursor-not-allowed' :
                  'text-gray-400 hover:text-green-600 hover:bg-green-50'
                }`}
                title="Good result"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onFeedback(-1)}
                disabled={feedbackGiven != null}
                className={`p-1.5 rounded transition-colors ${
                  feedbackGiven === -1 ? 'bg-red-100 text-red-600' :
                  feedbackGiven != null ? 'text-gray-200 cursor-not-allowed' :
                  'text-gray-400 hover:text-red-600 hover:bg-red-50'
                }`}
                title="Bad result"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            </div>
            {data.execution_ms != null && (
              <span className="text-[10px] text-gray-400">
                <Clock className="h-3 w-3 inline mr-0.5" />{data.execution_ms}ms
              </span>
            )}
          </div>

          {/* Follow-up suggestions */}
          {data.suggested_followups?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.suggested_followups.slice(0, 3).map((q, i) => (
                <button
                  key={i}
                  onClick={() => onFollowUp(q)}
                  className="px-2.5 py-1 text-[10px] bg-violet-50 text-violet-700 rounded-full border border-violet-100 hover:bg-violet-100 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== Visual Condition Builder ====================

const VisualConditionBuilder = ({ conditions, setConditions, availableColumns, columnsLoading, columnMap = {} }) => {
  const addCondition = () => {
    setConditions([...conditions, { column: '', operator: '=', value: '', logic_operator: 'AND' }]);
  };

  const updateCondition = (idx, field, value) => {
    const updated = [...conditions];
    updated[idx] = { ...updated[idx], [field]: value };
    setConditions(updated);
  };

  const removeCondition = (idx) => {
    if (conditions.length <= 1) {
      setConditions([]);
    } else {
      setConditions(conditions.filter((_, i) => i !== idx));
    }
  };

  const operators = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'NOT LIKE', 'IN', 'NOT IN', 'BETWEEN'];

  const getTypeBadge = (colName) => {
    const info = columnMap[colName];
    if (!info) return null;
    const colors = {
      text: 'bg-purple-100 text-purple-600',
      number: 'bg-blue-100 text-blue-600',
      decimal: 'bg-blue-100 text-blue-600',
      date: 'bg-orange-100 text-orange-600',
      datetime: 'bg-orange-100 text-orange-600',
      boolean: 'bg-green-100 text-green-600',
    };
    return (
      <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${colors[info.category] || 'bg-gray-100 text-gray-600'}`}>
        {info.category}
      </span>
    );
  };

  const getPlaceholder = (cond) => {
    if (cond.operator === 'IN' || cond.operator === 'NOT IN') return "val1, val2, val3";
    if (cond.operator === 'BETWEEN') return "min, max";
    const info = columnMap[cond.column];
    if (!info) return "value";
    switch (info.category) {
      case 'text': return "'text value' (use quotes)";
      case 'number': case 'decimal': return "numeric value (no quotes)";
      case 'date': return "YYYY-MM-DD";
      case 'datetime': return "YYYY-MM-DD HH:MM:SS";
      case 'boolean': return "1 or 0";
      default: return "value";
    }
  };

  return (
    <div className="space-y-2">
      {conditions.length === 0 && (
        <button onClick={addCondition} className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1">
          <Plus className="h-4 w-4" /><span>Add condition</span>
        </button>
      )}
      {conditions.map((cond, idx) => (
        <div key={idx} className="flex items-center space-x-2">
          {idx > 0 && (
            <select
              value={cond.logic_operator || 'AND'}
              onChange={(e) => updateCondition(idx, 'logic_operator', e.target.value)}
              className="px-2 py-1.5 border rounded text-xs font-medium w-16"
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          )}
          {idx === 0 && <div className="w-16 flex-shrink-0"><span className="text-xs text-gray-400">WHERE</span></div>}

          {/* Column */}
          <select
            value={cond.column}
            onChange={(e) => updateCondition(idx, 'column', e.target.value)}
            className="flex-1 px-3 py-1.5 border rounded text-sm max-w-[200px]"
          >
            <option value="">Select column...</option>
            {availableColumns.map(col => (
              <option key={col} value={col}>{col}{columnMap[col] ? ` (${columnMap[col].category})` : ''}</option>
            ))}
          </select>

          {/* Type badge */}
          {cond.column && getTypeBadge(cond.column)}

          {/* Operator */}
          <select
            value={cond.operator}
            onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
            className="px-2 py-1.5 border rounded text-sm w-28"
          >
            {operators.map(op => <option key={op} value={op}>{op}</option>)}
          </select>

          {/* Value */}
          <input
            type="text"
            value={Array.isArray(cond.value) ? cond.value.join(', ') : cond.value}
            onChange={(e) => {
              const op = cond.operator;
              if (op === 'IN' || op === 'NOT IN' || op === 'BETWEEN') {
                // Parse comma-separated values
                const vals = e.target.value.split(',').map(v => {
                  v = v.trim();
                  const n = Number(v);
                  return isNaN(n) ? v : n;
                });
                updateCondition(idx, 'value', vals);
              } else {
                let val = e.target.value;
                const n = Number(val);
                updateCondition(idx, 'value', val === '' ? '' : isNaN(n) ? val : n);
              }
            }}
            className="flex-1 px-3 py-1.5 border rounded text-sm"
            placeholder={getPlaceholder(cond)}
          />

          <button onClick={() => removeCondition(idx)} className="p-1 text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      {conditions.length > 0 && (
        <button onClick={addCondition} className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 mt-1">
          <Plus className="h-3 w-3" /><span>Add condition</span>
        </button>
      )}
    </div>
  );
};


// ==================== Column Picker ====================

const ColumnPicker = ({ availableColumns, selectedColumns, setSelectedColumns, systemColumns, onClose }) => {
  const userColumns = availableColumns.filter(c => !systemColumns.includes(c)).sort((a, b) => a.localeCompare(b));

  const toggle = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  return (
    <div className="border rounded-lg bg-gray-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">Select Columns</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedColumns(userColumns)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Select All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => setSelectedColumns([])}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Clear
          </button>
          <button onClick={onClose} className="p-0.5 text-gray-400 hover:text-gray-600 ml-2">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
        {userColumns.map(col => (
          <button
            key={col}
            onClick={() => toggle(col)}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              selectedColumns.length === 0 || selectedColumns.includes(col)
                ? 'bg-blue-100 border-blue-300 text-blue-700'
                : 'bg-white border-gray-200 text-gray-500 hover:border-blue-300'
            }`}
          >
            {col}
          </button>
        ))}
      </div>
      {selectedColumns.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">{selectedColumns.length} of {userColumns.length} columns selected</p>
      )}
    </div>
  );
};


// ==================== Query Error Display ====================

const QueryErrorDisplay = ({ error, columnMap, textFilter, conditions, queryMode }) => {
  // Try to extract column names mentioned in the error to show their types
  const mentionedColumns = [];
  if (columnMap && Object.keys(columnMap).length > 0) {
    // Check conditions
    const activeConds = queryMode === 'visual' ? conditions : [];
    activeConds.forEach(c => {
      if (c.column && columnMap[c.column]) {
        mentionedColumns.push({ name: c.column, ...columnMap[c.column] });
      }
    });
    // Also scan the text filter for column names
    if (textFilter) {
      Object.entries(columnMap).forEach(([name, info]) => {
        if (textFilter.includes(name) && !mentionedColumns.find(c => c.name === name)) {
          mentionedColumns.push({ name, ...info });
        }
      });
    }
  }

  const getCategoryIcon = (cat) => {
    const icons = {
      text: '𝐓', number: '#', decimal: '#.#', date: '📅', datetime: '🕐', boolean: '✓/✗', array: '[]'
    };
    return icons[cat] || '?';
  };

  const getCategoryColor = (cat) => {
    const colors = {
      text: 'bg-purple-100 text-purple-700',
      number: 'bg-blue-100 text-blue-700',
      decimal: 'bg-blue-100 text-blue-700',
      date: 'bg-orange-100 text-orange-700',
      datetime: 'bg-orange-100 text-orange-700',
      boolean: 'bg-green-100 text-green-700',
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
      <div className="p-4 flex items-start space-x-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800">Query Error</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      </div>
      
      {/* Column type hints if we can identify the relevant columns */}
      {mentionedColumns.length > 0 && (
        <div className="px-4 pb-4 pt-0">
          <div className="bg-white border border-red-100 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Column types in your filter:</p>
            <div className="space-y-1.5">
              {mentionedColumns.map((col, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${getCategoryColor(col.category)}`}>
                      {getCategoryIcon(col.category)} {col.category}
                    </span>
                    <code className="text-xs font-mono text-gray-800">{col.name}</code>
                    {col.type && (
                      <span className="text-xs text-gray-400">({col.type})</span>
                    )}
                  </div>
                  {col.hint && (
                    <code className="text-xs text-gray-500 font-mono">{col.hint}</code>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default QueryExplorer;